import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, GitMerge, AlertCircle, ArrowRight, Info } from 'lucide-react';
import { extractErrorMessage } from '@/lib/errorUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Manual Player Merge dialog (per-team).
 *
 * Why: the auto-detect in Settings → Player Cleanup only groups players
 * whose names are ≥88% similar. Intentional spelling corrections (e.g.
 * "Daniel Ebanks" → "Daniye'l Ebanks" — only 85% similar) fall below
 * the threshold and leave the old spelling attached to historical
 * stats_games while the new spelling shows 0 GP on the public team card.
 *
 * This dialog lists EVERY distinct player spelling tied to one team
 * (across live roster + historical stats_games) and lets the organizer
 * pick 2+ rows to collapse into one canonical spelling.
 */
export function MergePlayersDialog({ open, onOpenChange, eventId, team, onMerged }) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [players, setPlayers] = useState([]); // {name, jersey, games_count, in_roster}
  const [selectedKeys, setSelectedKeys] = useState(new Set()); // "name|jersey"
  const [canonicalKey, setCanonicalKey] = useState(''); // "name|jersey"

  const _key = (p) => `${p.name}|${p.jersey}`;

  useEffect(() => {
    if (!open || !eventId || !team || !team.id) return;
    setLoading(true);
    setSelectedKeys(new Set());
    setCanonicalKey('');
    const token = localStorage.getItem('session_token');
    fetch(
      `${API_URL}/api/player-merges/team-spellings?event_id=${encodeURIComponent(eventId)}&team_id=${encodeURIComponent(team.id)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          toast.error(extractErrorMessage(data, 'Could not load players'));
          setPlayers([]);
          return;
        }
        setPlayers(data.players || []);
      })
      .catch((e) => {
        toast.error(extractErrorMessage(e, 'Could not load players'));
        setPlayers([]);
      })
      .finally(() => setLoading(false));
  }, [open, eventId, team]);

  const toggleSelected = (p) => {
    const k = _key(p);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(k)) {
        next.delete(k);
        // If we're removing the canonical, clear it
        if (canonicalKey === k) setCanonicalKey('');
      } else {
        next.add(k);
        // Auto-pick canonical: the first selection OR the one with most games
        if (!canonicalKey) {
          setCanonicalKey(k);
        } else {
          const current = players.find((pp) => _key(pp) === canonicalKey);
          if (!current || p.games_count > current.games_count) {
            setCanonicalKey(k);
          }
        }
      }
      return next;
    });
  };

  const selectedPlayers = useMemo(
    () => players.filter((p) => selectedKeys.has(_key(p))),
    [players, selectedKeys],
  );

  const canonical = useMemo(
    () => players.find((p) => _key(p) === canonicalKey) || null,
    [players, canonicalKey],
  );

  const variants = useMemo(
    () => selectedPlayers.filter((p) => _key(p) !== canonicalKey),
    [selectedPlayers, canonicalKey],
  );

  const canApply = selectedPlayers.length >= 2 && !!canonical && variants.length >= 1;

  const handleApply = async () => {
    if (!canApply) return;
    setApplying(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/player-merges/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          team_name: team.name,
          canonical_name: canonical.name,
          canonical_jersey: canonical.jersey || '',
          variant_names: variants.map((v) => v.name),
          event_ids: [eventId],
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(extractErrorMessage(data, 'Merge failed'));
        return;
      }
      const total = variants.length;
      toast.success(
        `Merged ${total} spelling${total === 1 ? '' : 's'} into "${canonical.name}". ` +
        `Updated ${data.games_updated} game${data.games_updated === 1 ? '' : 's'} and ${data.rosters_updated} roster${data.rosters_updated === 1 ? '' : 's'}.`,
      );
      if (onMerged) onMerged();
      onOpenChange(false);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Merge failed'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="merge-players-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="w-5 h-5 text-[#00247D]" />
            Merge duplicate players — {team && team.name}
          </DialogTitle>
          <DialogDescription>
            Fix spelling splits where one player appears as two separate rows.
            Pick 2+ entries that are the same player, choose which spelling to
            keep, and all historical stats will be reassigned to that spelling.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#00247D]" />
          </div>
        ) : players.length === 0 ? (
          <div className="py-10 text-center text-slate-500 text-sm">
            No player records found for this team yet.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start gap-2 text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded-md px-3 py-2">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <strong>Tip:</strong> the row with the most games played is pre-picked as the canonical spelling.
                Click any row to change the canonical. Rows with <em>0 GP</em> are usually the empty placeholder created after a rename.
              </div>
            </div>

            <div className="space-y-1.5" data-testid="player-spellings-list">
              {players.map((p) => {
                const k = _key(p);
                const isSelected = selectedKeys.has(k);
                const isCanonical = canonicalKey === k;
                return (
                  <div
                    key={k}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-md border transition-all ${
                      isCanonical
                        ? 'bg-[#00247D] text-white border-[#00247D] shadow'
                        : isSelected
                          ? 'bg-amber-50 border-amber-300 text-slate-800'
                          : 'bg-white border-slate-200 text-slate-700 hover:border-[#00247D]/40'
                    }`}
                    data-testid={`player-spelling-row-${p.name}`}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleSelected(p)}
                      className={isCanonical ? 'border-white data-[state=checked]:bg-amber-400 data-[state=checked]:text-[#00247D]' : ''}
                      data-testid={`player-spelling-check-${p.name}`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (!isSelected) toggleSelected(p);
                        else setCanonicalKey(k);
                      }}
                      className="flex-1 min-w-0 flex items-center gap-3 text-left"
                      data-testid={`player-spelling-pick-${p.name}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{p.name}</div>
                        <div className={`text-[11px] ${isCanonical ? 'text-amber-200' : 'text-slate-500'}`}>
                          #{p.jersey || '—'} · {p.games_count} GP
                          {p.in_roster ? ' · in current roster' : ''}
                        </div>
                      </div>
                      {isCanonical && (
                        <Badge className="bg-amber-400 text-[#00247D] hover:bg-amber-400 text-[10px] uppercase tracking-wider">
                          Keep this
                        </Badge>
                      )}
                      {!isCanonical && isSelected && (
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-amber-400 text-amber-700">
                          Merge in
                        </Badge>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>

            {selectedPlayers.length >= 2 && canonical && (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-sm" data-testid="merge-preview">
                <div className="font-semibold text-slate-800 mb-1.5 flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  Preview
                </div>
                <div className="flex items-center gap-2 flex-wrap text-xs text-slate-700">
                  {variants.map((v) => (
                    <span key={_key(v)} className="px-2 py-1 bg-white border border-slate-200 rounded">
                      {v.name} <span className="text-slate-400">· {v.games_count} GP</span>
                    </span>
                  ))}
                  <ArrowRight className="w-4 h-4 text-slate-500" />
                  <span className="px-2 py-1 bg-[#00247D] text-white rounded font-semibold">
                    {canonical.name}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 mt-2">
                  All historical game stats and roster entries will be rewritten
                  so they point to <strong>{canonical.name}</strong>. This cannot be undone automatically.
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={applying}
            data-testid="merge-players-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleApply}
            disabled={!canApply || applying}
            className="bg-[#00247D] hover:bg-[#001A5C] text-white"
            data-testid="merge-players-apply-btn"
          >
            {applying ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Merging…</>
            ) : (
              <><GitMerge className="w-4 h-4 mr-2" /> Merge selected ({variants.length})</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
